import { Builder, withChildren } from '@builder.io/react';

import Hero from '@/components/builder/Hero/Hero';

import BenefitsGrid from '@/components/builder/BenefitsGrid/BenefitsGrid';
import HalfGridFeature from '@/components/builder/HalfGridFeature/HalfGridFeature';
import BackgroundColorContainer from '@/components/builder/BackgroundColorContainer/BackgroundColorContainer';
import HalfPhotoGridFeature from '@/components/builder/HalfPhotoGridFeature/HalfPhotoGridFeature';
import HighlightCarousel from '@/components/builder/HighlightCarousel/HighlightCarousel';
import FAQSection from '@/components/builder/FAQSection/FAQSection';
import StaticHero from '@/components/builder/StaticHero/StaticHero';
import PerksGrid from '@/components/builder/PerksGrid/PerksGrid';
import RestrictSize from '@/components/builder/RestrictSize/RestrictSize';
import AspectRatioImage from '@/components/common/AspectRatioImage/AspectRatioImage';
import NewArrivalsCarousel from '@/components/builder/NewArrivalsCarousel/NewArrivalsCarousel';
import VideoHero from '@/components/builder/VideoHero/VideoHero';
import HighlightCardMini from '@/components/builder/HighlightCardMini/HighlightCardMini';
import HighlightCardMiniList from '@/components/builder/HighlightCardMiniList/HighlightCardMiniList';
import HighlightCardFull from '@/components/builder/HighlightCardFull/HighlightCardFull';
import CompleteTheLookRecommendationGrid from '@/components/builder/CompleteTheLookRecommendationGrid/CompleteTheLookRecommendationGrid';
import LeadCapturer from '@/components/builder/LeadCaptureForm/LeadCaptureForm';
import BuilderSiteBanner from '@/components/builder/BuilderSiteBanner/BuilderSiteBanner';
import StaticHeroV2 from '@/components/builder/StaticHeroV2/StaticHeroV2';
import SquareCardGroup from './SquareCardGroup/SquareCardGroup';
import SquareCard from './SquareCard/SquareCard';

import PlanTierTable from '@/components/builder/PlanTierTable/PlanTierTable';
import PromoCodeInput from '@/components/builder/PromoCodeInput/PromoCodeInput';
// HYGENINJECT-IMPORT

export type CMSPlainImageData = {
  image: string;
  imageAltText: string;
  imageHeight: number;
  imageWidth: number;
};

/**
 * Image data generated and stored onto Builder CMS
 * using the `getBaseImageFields` method on responsive
 * images
 */
export type CMSResponsiveImageData = {
  mobile: CMSPlainImageData;
  desktop: CMSPlainImageData;
};

const ImageInputs = [
  {
    name: 'image',
    type: 'file',
    friendlyName: 'Image',
    helperText: 'Image to show the user',
    allowedFileTypes: ['jpeg', 'png', 'svg', 'gif'],
    required: true
  },
  {
    name: 'imageAltText',
    type: 'text',
    friendlyName: 'ImageAlt Text',
    helperText: 'Text for screen readers. Describe the icon/image in words',
    required: true
  },
  {
    name: 'imageHeight',
    type: 'number',
    friendlyName: 'Image/Icon height',
    helperText: 'Used to calculate aspect ratio. Not used for actual sizing',
    required: true
  },
  {
    name: 'imageWidth',
    type: 'number',
    friendlyName: 'Image/Icon width',
    helperText: 'Used to calculate aspect ratio. Not used for actual sizing',
    required: true
  }
];

const HighlightCardMiniInputs = [
  {
    name: 'headline',
    type: 'text',
    friendlyName: 'Top header text',
    helperText:
      'Displayed at the very top in large italics, giving this a headline or title',
    required: true
  },
  {
    name: 'description',
    type: 'longText',
    helperText: 'You can add up to 200 characters to this field',
    friendlyName: 'Description',
    max: 200,
    required: true
  },
  {
    name: 'ctaText',
    type: 'text',
    friendlyName: 'CTA Text',
    helperText: 'What should the link at the bottom say?',
    required: false
  },
  {
    name: 'ctaLink',
    type: 'text',
    friendlyName: 'CTA Link',
    helperText:
      'Relative link starts with "/"; absolute link should be full including "http"',
    required: false
  },
  {
    name: 'image',
    type: 'object',
    required: true,
    friendlyName: 'MainImage',
    helperText: 'Select image to display for this highlight card',
    subFields: ImageInputs
  }
];

const SquareCardInputs = [
  {
    name: 'loggedOut',
    type: 'object',
    required: true,
    friendlyName: 'Logged Out Content',
    helperText: 'Image, text, and CTA to display when the user is logged out',
    subFields: [
      {
        name: 'title',
        type: 'text',
        friendlyName: 'Title (logged out)',
        helperText: 'Displayed at the top in large italics',
        required: true,
        defaultValue: 'default title'
      },
      {
        name: 'description',
        type: 'text',
        friendlyName: 'Description (logged out)',
        helperText:
          'Displayed below the title. You can add blank lines to match the height of horizontal neighbors by adding one or many "\\n"s to the end of this text.',
        required: true
      },
      {
        name: 'image',
        type: 'object',
        required: true,
        friendlyName: 'Image (logged out)',
        helperText: 'Image fields',
        subFields: ImageInputs
      },
      {
        name: 'cta',
        type: 'object',
        required: true,
        friendlyName: 'Call-to-action (logged out)',
        helperText: 'Copy and link destination for CTA',
        subFields: [
          {
            name: 'href',
            type: 'text',
            required: true,
            friendlyName: 'Link destination',
            helperText: 'Destination of the CTA link'
          },
          {
            name: 'text',
            type: 'text',
            required: true,
            friendlyName: 'Link text',
            helperText:
              'Text of the CTA link. If it needs to extend to the next line, put "\\n" where the line should break.'
          }
        ]
      }
    ]
  },
  {
    name: 'loggedIn',
    type: 'object',
    required: false,
    friendlyName: 'Logged In Content',
    helperText: 'Image, text, and CTA to display when the user is logged in',
    subFields: [
      {
        name: 'title',
        type: 'text',
        friendlyName: 'Title (logged in)',
        helperText: 'Displayed at the top in large italics',
        required: false
      },
      {
        name: 'description',
        type: 'text',
        friendlyName: 'Description (logged in)',
        helperText:
          'Displayed below the title. You can add blank lines to match the height of horizontal neighbors by adding one or many "\\n"s to the end of this text.',
        required: false
      },
      {
        name: 'image',
        type: 'object',
        required: false,
        friendlyName: 'Image (logged in)',
        helperText: 'Image fields',
        subFields: ImageInputs
      },
      {
        name: 'cta',
        type: 'object',
        required: false,
        friendlyName: 'Call-to-action (logged in)',
        helperText: 'Copy and link destination for CTA',
        subFields: [
          {
            name: 'href',
            type: 'text',
            required: true,
            friendlyName: 'Link destination',
            helperText: 'Destination of the CTA link'
          },
          {
            name: 'text',
            type: 'text',
            required: true,
            friendlyName: 'Link text',
            helperText: 'Text of the CTA link'
          }
        ]
      }
    ]
  },
  {
    name: 'slightlySmallerCopy',
    type: 'boolean',
    friendlyName: 'Slightly smaller copy on desktop?',
    helperText:
      'If enabled, the description and CTA copy will be slightly smaller on larger (i.e. non-mobile) devices. This could be helpful, for example, if there are more than 3 of these cards next to each other horizontally.',
    defaultValue: false,
    required: false,

    // Disables this input if the component is inside a SquareCardGroup,
    // since the group itself has an input to control this value for all of its children.
    // Note: yes, it has to be a string expression like this, unfortunately.
    showIf: `parentElements.every(el => el && el.component && el.component.name !== 'SquareCardGroup')`
  }
];

// How to use this file
// See `inputs` documentation at this link:
// https://www.builder.io/c/docs/custom-components-input-types

Builder.registerComponent(Hero, {
  name: 'Hero Banner',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS ClassName',
      required: false,
      helperText:
        '[OPTIONAL] - Used if a developer needs to style something with specialized styling'
    },
    {
      name: 'displayText',
      type: 'richText',
      friendlyName: 'Text',
      required: true,
      helperText: 'The large font text to be displayed on top of the image'
    },
    {
      name: 'halfVariant',
      type: 'boolean',
      friendlyName: 'Restrict width of "float" to 50%?',
      required: false,
      helperText:
        'Used for Hero images that expect text to only be 50% of the total width',
      defaultValue: false
    },
    {
      name: 'desktopLeftPosition',
      type: 'number',
      friendlyName: '% units to move from the left',
      required: true,
      helperText:
        '0 is flush with left side. 100 is way far off the right. 200 is a bad idea. 300 is right out',
      defaultValue: 5,
      max: 100,
      min: 0
    },
    {
      name: 'desktopTopPosition',
      type: 'number',
      friendlyName: '% units to move from the top/bottom',
      required: true,
      helperText:
        '50 will keep the floating text + button dead center. Lower number = higher up',
      defaultValue: 40,
      max: 100,
      min: 0
    },
    {
      name: 'buttonText',
      type: 'text',
      friendlyName: 'Button Text',
      required: false,
      helperText:
        'Text for the button. If this is blank, no button will be shown'
    },
    {
      name: 'buttonLink',
      type: 'text',
      friendlyName: 'Button destination URL',
      required: false,
      helperText: 'Link to direct the user when the button is clicked'
    },
    {
      name: 'image',
      type: 'file',
      friendlyName: 'Image/Photo [DESKTOP]',
      allowedFileTypes: ['jpeg', 'png', 'svg', 'gif'],
      required: true,
      helperText: 'Image to show on desktop'
    },
    {
      name: 'imgWidth',
      type: 'number',
      friendlyName: 'Width of Image (px)',
      required: true,
      helperText:
        '[NOT USED IN SIZING ON PAGE] - Used to calculate aspect ratio to prevent skewing'
    },
    {
      name: 'imgHeight',
      type: 'number',
      friendlyName: 'Height of Image (px)',
      required: true,
      helperText:
        '[NOT USED IN SIZING ON PAGE] - Used to calculate aspect ratio to prevent skewing'
    },
    {
      name: 'imageAltText',
      type: 'text',
      friendlyName: 'Image alt text (accessibility)',
      required: true,
      helperText:
        'If a blind person were to look at this image, what would you want the screen reader to say about it?'
    },
    {
      name: 'imageMobile',
      type: 'file',
      friendlyName: 'Image/Photo [MOBILE]',
      allowedFileTypes: ['jpeg', 'png', 'svg', 'gif'],
      required: true,
      helperText: 'Image to show on mobile devices'
    },
    {
      name: 'imageMobileWidth',
      type: 'number',
      friendlyName: '[MOBILE] Width of Image (px)',
      required: true,
      helperText:
        '[NOT USED IN SIZING ON PAGE] - Used to calculate aspect ratio to prevent skewing'
    },
    {
      name: 'imageMobileHeight',
      type: 'number',
      friendlyName: '[MOBILE] Height of Image (px)',
      required: true,
      helperText:
        '[NOT USED IN SIZING ON PAGE] - Used to calculate aspect ratio to prevent skewing'
    },
    {
      name: 'imageMobileAltText',
      type: 'text',
      friendlyName: '[MOBILE] Image alt text (accessibility)',
      required: true,
      helperText:
        'If a blind person were to look at this image, what would you want the screen reader to say about it?'
    },
    {
      name: 'showNotch',
      type: 'boolean',
      friendlyName: 'Show notch?',
      required: false,
      defaultValue: true,
      helperText:
        'Should we show a centered notch that overlays on top of the image?'
    }
  ]
});

Builder.registerComponent(BenefitsGrid, {
  name: 'Benefits Grid',
  inputs: [
    {
      name: 'className',
      type: 'text'
    },
    {
      name: 'topHeadline',
      type: 'text'
    },
    {
      name: 'gridItems',
      type: 'list',
      subFields: [
        {
          name: 'className',
          type: 'text',
          friendlyName: 'CSS className',
          helperText:
            'Used for Developer override styling. Keep blank if not needed',
          required: false,
          defaultValue: ''
        },
        {
          name: 'image',
          type: 'file',
          friendlyName: 'Image/Icon',
          helperText:
            'Please use an SVG or PNG file, as this component requires transparency',
          allowedFileTypes: ['.svg', '.png'],
          required: true
        },
        {
          name: 'imageAltText',
          type: 'text',
          friendlyName: 'ImageAlt Text',
          helperText:
            'Text for screen readers. Describe the icon/image in words',
          required: true
        },
        {
          name: 'imageHeight',
          type: 'number',
          friendlyName: 'Image/Icon height',
          helperText:
            'Used to calculate aspect ratio. Not used for actual sizing',
          required: true
        },
        {
          name: 'imageWidth',
          type: 'number',
          friendlyName: 'Image/Icon width',
          helperText:
            'Used to calculate aspect ratio. Not used for actual sizing',
          required: true
        },
        {
          name: 'headline',
          type: 'text',
          friendlyName: 'Headline Text',
          helperText: 'Large headline text to show end user',
          required: true
        },
        {
          name: 'longText',
          type: 'text',
          friendlyName: 'Text',
          helperText: 'Longform text to show',
          required: true
        }
      ]
    }
  ]
});

Builder.registerComponent(HalfGridFeature, {
  name: 'HalfGridFeature',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'showBottomLineDivider',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Shows a $red-300 line divider at the bottom if true',
      required: false
    },
    {
      name: 'items',
      type: 'list',
      friendlyName: 'List items (MAX OF 2)',
      helperText: 'Keep this at 2 items. Will ignore any items past 2',
      subFields: [
        {
          name: 'headline',
          type: 'text',
          friendlyName: 'Headline',
          helperText: 'Headline for this featured benefit',
          required: true
        },
        {
          name: 'text',
          type: 'text',
          friendlyName: 'Feature Copy/Text',
          helperText: 'Sales copy to display',
          required: true
        }
      ]
    }
  ]
});

Builder.registerComponent(withChildren(BackgroundColorContainer), {
  name: 'BackgroundColorContainer',
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'You can put components inside this container. These components will also be restricted to 1400px in max width, centered'
        }
      }
    }
  ],
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'color',
      type: 'color',
      friendlyName: 'Background Color',
      required: true,
      helperText: 'Color to use as background'
    }
  ]
});

Builder.registerComponent(HalfPhotoGridFeature, {
  name: 'HalfPhotoGridFeature',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        {
          name: 'className',
          type: 'text',
          friendlyName: 'CSS className',
          helperText:
            'Used for Developer override styling. Keep blank if not needed',
          required: false,
          defaultValue: ''
        },
        {
          name: 'image',
          type: 'object',
          required: true,
          friendlyName: 'Image',
          helperText: 'Image to show',
          subFields: ImageInputs
        },
        {
          name: 'headline',
          type: 'text',
          friendlyName: 'Headline Text',
          helperText: 'Large headline text to show end user',
          required: true
        },
        {
          name: 'text',
          type: 'text',
          friendlyName: 'Text',
          helperText: 'Longform text to show',
          required: true
        },
        {
          name: 'link',
          type: 'text',
          friendlyName: 'Link to navigate to on click',
          helperText: 'When the user clicks on this, where should they go?',
          required: true
        }
      ]
    }
  ]
});

Builder.registerComponent(HighlightCarousel, {
  name: 'HighlightCarousel',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'desktopItems',
      type: 'list',
      subFields: [
        ...ImageInputs,
        {
          name: 'link',
          type: 'text',
          friendlyName: 'Link to navigate to on click',
          helperText: 'When the user clicks on this, where should they go?',
          required: false
        }
      ],
      helperText: 'Desktop carousel items',
      required: true
    },
    {
      name: 'mobileItems',
      type: 'list',
      subFields: [
        ...ImageInputs,
        {
          name: 'link',
          type: 'text',
          friendlyName: 'Link to navigate to on click',
          helperText: 'When the user clicks on this, where should they go?',
          required: false
        }
      ],
      helperText: 'Mobile carousel items',
      required: true
    }
  ]
});

Builder.registerComponent(CompleteTheLookRecommendationGrid, {
  name: 'CompleteTheLookRecommendationGrid',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    }
  ]
});

Builder.registerComponent(FAQSection, {
  name: 'FAQSection',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'headline',
      type: 'text',
      friendlyName: 'Top header text',
      helperText:
        'Displayed at the very top in pretty italics and biiig text, giving this a headline or title',
      required: true
    },
    {
      name: 'questions',
      type: 'list',
      subFields: [
        {
          name: 'question',
          type: 'text',
          friendlyName: 'Question',
          helperText: 'ex: "Are Vivrelle bags awesome? Or are they awesome?"',
          required: true
        },
        {
          name: 'answer',
          type: 'richText',
          friendlyName: 'Answer',
          helperText:
            'Answer to said question (ex: "Of course they are awesome!")',
          required: true
        }
      ],
      helperText: 'FAQ Q/As to show the user',
      required: true
    },
    {
      name: 'ctaText',
      type: 'text',
      friendlyName: 'CTA Text',
      helperText: 'What should the link at the bottom say?',
      required: false
    },
    {
      name: 'ctaLink',
      type: 'text',
      friendlyName: 'CTA Link',
      helperText: 'Relative link (starts with "/" to a URL)',
      required: false
    }
  ]
});

Builder.registerComponent(StaticHero, {
  name: 'StaticHero',
  hideFromInsertMenu: true,
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'priority',
      type: 'boolean',
      required: false,
      friendlyName: 'Give load priority?',
      helperText:
        'If this image is above the fold on page, please set this value to "true"'
    },
    {
      name: 'image',
      type: 'object',
      required: true,
      friendlyName: 'Desktop/Mobile images',
      helperText: 'Select images to display for this partner',
      subFields: [
        {
          name: 'desktop',
          type: 'object',
          required: true,
          friendlyName: 'Desktop Image',
          helperText: 'Desktop Image fields',
          subFields: ImageInputs
        },
        {
          name: 'mobile',
          type: 'object',
          required: true,
          friendlyName: 'Mobile Image',
          helperText: 'Mobile Image fields',
          subFields: ImageInputs
        }
      ]
    },
    {
      name: 'linkUrl',
      type: 'string',
      required: false,
      friendlyName: 'URL to link to',
      helperText:
        'Please use a relative URL that starts with "/" - When the user clicks on this, where should they go? '
    }
  ]
});

Builder.registerComponent(StaticHeroV2, {
  name: 'v2: StaticHero', // Builder ignores anything before colon (https://www.builder.io/c/docs/custom-components-versioning)
  inputs: [
    {
      name: 'priority',
      type: 'boolean',
      required: false,
      friendlyName: 'Give image load priority?',
      helperText:
        'If this image is above the fold on page, please set this value to "true"'
    },
    {
      name: 'loggedOut',
      type: 'object',
      required: true,
      friendlyName: 'Logged Out Content',
      helperText:
        'Image to display and link destination when user is logged out',
      subFields: [
        {
          name: 'image',
          type: 'object',
          required: true,
          friendlyName: 'Desktop/Mobile images (logged out)',
          helperText: 'Select images to display for this partner',
          subFields: [
            {
              name: 'desktop',
              type: 'object',
              required: true,
              friendlyName: 'Desktop Image',
              helperText: 'Desktop Image fields',
              subFields: ImageInputs
            },
            {
              name: 'mobile',
              type: 'object',
              required: true,
              friendlyName: 'Mobile Image',
              helperText: 'Mobile Image fields',
              subFields: ImageInputs
            }
          ]
        },
        {
          name: 'linkUrl',
          type: 'string',
          required: false,
          friendlyName: 'URL to link to when logged out',
          helperText:
            'Please use a relative URL that starts with "/" - When the user clicks on this, where should they go? '
        }
      ]
    },
    {
      name: 'loggedIn',
      type: 'object',
      required: false,
      friendlyName: 'Logged In Content',
      helperText:
        'Image to display and link destination when user is logged in',
      subFields: [
        {
          name: 'image',
          type: 'object',
          required: false,
          friendlyName: 'Desktop/Mobile images (logged in)',
          helperText: 'Select images to display for this partner',
          subFields: [
            {
              name: 'desktop',
              type: 'object',
              required: true,
              friendlyName: 'Desktop Image',
              helperText: 'Desktop Image fields',
              subFields: ImageInputs
            },
            {
              name: 'mobile',
              type: 'object',
              required: true,
              friendlyName: 'Mobile Image',
              helperText: 'Mobile Image fields',
              subFields: ImageInputs
            }
          ]
        },
        {
          name: 'linkUrl',
          type: 'string',
          required: false,
          friendlyName: 'URL to link to when logged in',
          helperText:
            'Please use a relative URL that starts with "/" - When the user clicks on this, where should they go? '
        }
      ]
    }
  ]
});

Builder.registerComponent(PerksGrid, {
  name: 'PerksGrid',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'perks',
      type: 'list',
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          friendlyName: 'Perk Name',
          helperText: 'Name of the partner'
        },
        {
          name: 'logo',
          type: 'object',
          required: true,
          friendlyName: 'Desktop/Mobile images',
          helperText:
            'Select images to display for this partner (ideal aspect ratio 3:2)',
          subFields: [
            {
              name: 'desktop',
              type: 'object',
              required: true,
              friendlyName: 'Desktop Image',
              helperText: 'Desktop Image fields',
              subFields: ImageInputs
            },
            {
              name: 'mobile',
              type: 'object',
              required: true,
              friendlyName: 'Mobile Image',
              helperText: 'Mobile Image fields',
              subFields: ImageInputs
            }
          ]
        },
        {
          name: 'category',
          type: 'string',
          enum: [
            'Beauty & Wellness',
            'Fashion',
            'Foodies',
            'Home & Entertaining',
            'Hospitality',
            'Just for Moms',
            'Travel'
          ],
          helperText: 'Select the category for this perk',
          required: true
        },
        {
          name: 'description',
          type: 'longText',
          helperText:
            'This is the text that will appear to non-members and to members before the promo is revealed. You can add up to 200 characters to this field',
          friendlyName: 'Perk Description',
          max: 200,
          required: true
        },
        {
          name: 'descriptionButtonText',
          type: 'string',
          helperText:
            'This text will appear in the button when the description is shown and the promo information is hidden.',
          friendlyName: 'Description Button Text',
          required: true
        },
        {
          name: 'promoText',
          type: 'string',
          helperText:
            'What do members get with this perk? This text will appear for members after the description button is clicked',
          friendlyName: 'Promo Text',
          required: true
        },
        {
          name: 'promoButtonText',
          type: 'string',
          helperText:
            'This text will appear in the button when the promo text is shown. It should be used as a secondary CTA (for example to visit the partner website).',
          friendlyName: 'Promo Button Text',
          required: true
        },
        {
          name: 'promoCode',
          type: 'string',
          helperText:
            '[Optional] If this perk has a specific promo code for a user to copy, enter it here',
          friendlyName: 'Promo Code',
          required: false
        },
        {
          name: 'promoButtonLink',
          type: 'string',
          helperText: 'This is the URL that the Promo Button should link to.',
          friendlyName: 'Promo Button Link',
          required: true
        },
        {
          name: 'termsAndConditions',
          type: 'longText',
          helperText:
            '[Optional] Input any special terms or conditions that apply to this code here. It will appear in small text after the member is able to view the promo details.',
          friendlyName: 'Terms and Conditions',
          required: false
        }
      ]
    }
  ]
});

Builder.registerComponent(withChildren(RestrictSize), {
  name: 'RestrictSize',
  defaultChildren: [
    {
      '@type': '@builder.io/sdk:Element',
      component: {
        name: 'Text',
        options: {
          text: 'Components inside this container will be restricted to 1400px in width centered'
        }
      }
    }
  ],
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    }
  ]
});

Builder.registerComponent(AspectRatioImage, {
  name: 'AspectRatioImage',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'height',
      type: 'number',
      friendlyName: 'Image height',
      helperText:
        'Input the height of the image. This is used to calculate aspect ratio. Please ensure accuracy!',
      required: true
    },
    {
      name: 'width',
      type: 'number',
      friendlyName: 'Image width',
      helperText:
        'Input the width of the image. This is used to calculate aspect ratio. Please ensure accuracy!',
      required: true
    },
    {
      name: 'src',
      type: 'file',
      friendlyName: 'Image',
      helperText: 'Image to show the user',
      allowedFileTypes: ['jpeg', 'png', 'svg', 'gif'],
      required: true
    },
    {
      name: 'alt',
      type: 'text',
      friendlyName: 'ImageAlt Text',
      helperText: 'Text for screen readers. Describe the icon/image in words',
      required: true
    }
  ]
});

Builder.registerComponent(NewArrivalsCarousel, {
  name: 'NewArrivalsCarousel',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'headline',
      type: 'text',
      friendlyName: 'Headline',
      helperText: 'Text to show above the carousel. Optional',
      required: false
    },
    {
      name: 'mobileDisplayMode',
      type: 'text',
      friendlyName: 'Mobile Display Mode',
      helperText:
        'On mobile sizes, how would you like to display this component?',
      enum: ['carousel', 'two-per-row-grid']
    }
  ]
});

Builder.registerComponent(VideoHero, {
  name: 'VideoHero',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'videoSrc',
      type: 'file',
      friendlyName: 'Video File',
      helperText: 'Video file to be played',
      allowedFileTypes: ['mp4', 'mov', 'webm'],
      required: true
    },
    {
      name: 'videoFileFormat',
      type: 'string',
      friendlyName: 'Video file format',
      helperText: 'Put the file extension of the video into this field',
      enum: ['mp4', 'mov', 'webm'],
      required: true
    },
    {
      name: 'autoplay',
      type: 'boolean',
      friendlyName: 'Enable Auto-play',
      helperText:
        'If enabled, the video will auto play on page load. [WARNING] - if this value is enabled, ensure the video is MUTED in the next option, otherwise we will get penalized by search engines and unexpected behaviors built into all web browsers. We do not have control over this. Use at your own risk',
      defaultValue: false
    },
    {
      name: 'muted',
      type: 'boolean',
      friendlyName: 'Mute on Load?',
      helperText: 'By default, the video will be muted on load.',
      defaultValue: true
    },
    {
      name: 'poster',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg', 'gif'],
      friendlyName: 'Video Thumbnail',
      helperText: 'Image to show on top of the video before it is played'
    },
    {
      name: 'controls',
      type: 'boolean',
      friendlyName: 'Show controls?',
      helperText:
        'Show Play/Pause, rewind, forward, and mute buttons on the video element',
      required: true,
      defaultValue: false
    },
    {
      name: 'loop',
      type: 'boolean',
      friendlyName: 'Loop?',
      helperText: 'Restart video when it finishes?',
      defaultValue: false
    },
    {
      name: 'playsInline',
      type: 'boolean',
      friendlyName: 'Play inline on iOS?',
      helperText:
        'If false, the video will take over the whole screen on iOS when played. Otherwise it will play inline with the rest of the content',
      required: false,
      defaultValue: true
    },
    {
      name: 'allowDownload',
      type: 'boolean',
      friendlyName: 'Allow download?',
      helperText:
        'If enabled, allows for the video to be downloaded by the end user.',
      required: false,
      defaultValue: false
    },
    {
      name: 'displayOptions',
      type: 'string',
      friendlyName: 'Display Options',
      defaultValue: 'desktop-only',
      enum: ['desktop-only', 'mobile-only', 'desktop-and-mobile'],
      required: true
    }
  ]
});

Builder.registerComponent(HighlightCardMini, {
  name: 'HighlightCardMini',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    ...HighlightCardMiniInputs
  ]
});

Builder.registerComponent(HighlightCardMiniList, {
  name: 'HighlightCardMiniList',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'cards',
      type: 'list',
      subFields: HighlightCardMiniInputs
    }
  ]
});

Builder.registerComponent(SquareCard, {
  name: 'SquareCard',
  inputs: SquareCardInputs
});

Builder.registerComponent(SquareCardGroup, {
  name: 'SquareCardGroup',
  inputs: [
    {
      name: 'cards',
      type: 'list',
      friendlyName: 'Cards',
      subFields: SquareCardInputs
    },
    {
      name: 'carouselOnMobile',
      type: 'boolean',
      friendlyName: 'Carousel on mobile?',
      helperText:
        'If enabled, the cards will be displayed in a horizontal scrollable carousel on mobile',
      defaultValue: false,
      required: true,
      showIf: (options) => options.get('cards').length > 1
    },
    {
      name: 'slightlySmallerCopy',
      type: 'boolean',
      friendlyName: 'Slightly smaller copy on desktop?',
      helperText:
        'If enabled, the description and CTA copy will be slightly smaller on larger (i.e. non-mobile) devices. This could be helpful, for example, if there are more than 3 of these cards next to each other horizontally.'
    },
    {
      name: 'gapHoriz',
      type: 'number',
      friendlyName: 'Horizontal Gap Between Cards',
      helperText:
        'Amount of space (in pixels) between cards when arranged horizontally',
      defaultValue: 30,
      required: true
    },
    {
      name: 'gapVertical',
      type: 'number',
      friendlyName: 'Vertical Gap Between Cards',
      helperText:
        'Amount of space (in pixels) between cards when arranged vertically',
      defaultValue: 30,
      required: true,
      showIf: (options) => options.get('carouselOnMobile') == false
    }
  ]
});

Builder.registerComponent(HighlightCardFull, {
  name: 'HighlightCardFull',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'headline',
      type: 'text',
      friendlyName: 'Top header text',
      helperText:
        'Displayed at the very top in large italics, giving this a headline or title',
      required: true
    },
    {
      name: 'description',
      type: 'longText',
      helperText: 'You can add up to 200 characters to this field',
      friendlyName: 'Description',
      max: 200,
      required: true
    },
    {
      name: 'ctaText',
      type: 'text',
      friendlyName: 'CTA Text',
      helperText: 'What should the link at the bottom say?',
      required: false
    },
    {
      name: 'ctaLink',
      type: 'text',
      friendlyName: 'CTA Link',
      helperText:
        'Relative link starts with "/"; absolute link should be full including "http"',
      required: false
    },
    {
      name: 'image',
      type: 'object',
      required: true,
      friendlyName: 'Desktop/Mobile images',
      helperText: 'Select images to display for this highlight card',
      subFields: [
        {
          name: 'desktop',
          type: 'object',
          required: true,
          friendlyName: 'Desktop Image',
          helperText: 'Desktop Image fields',
          subFields: ImageInputs
        },
        {
          name: 'mobile',
          type: 'object',
          required: true,
          friendlyName: 'Mobile Image',
          helperText: 'Mobile Image fields',
          subFields: ImageInputs
        }
      ]
    },
    {
      name: 'isImageAlignedRight',
      type: 'boolean',
      friendlyName: 'Image align to the right for Desktop',
      helperText: 'If false, the image will default on the left side',
      required: false,
      defaultValue: false
    }
  ]
});

Builder.registerComponent(LeadCapturer, {
  name: 'Lead Capture Form',
  inputs: [
    {
      name: 'headerText',
      type: 'string',
      required: true,
      friendlyName: 'Form Header',
      helperText: 'Main header text displayed at the top of the form'
    },
    {
      name: 'subHeaderText',
      type: 'string',
      friendlyName: 'Form Subheader',
      helperText: 'Optional subheader text displayed below the main header'
    },
    {
      name: 'ctaButtonText',
      type: 'string',
      required: true,
      friendlyName: 'Submit Button Text',
      helperText: 'Text to display on the submit button',
      defaultValue: 'Submit'
    },
    {
      name: 'dropDownField',
      type: 'object',
      friendlyName: 'Dropdown Configuration',
      helperText: 'Optional dropdown field configuration',
      subFields: [
        {
          name: 'label',
          type: 'string',
          friendlyName: 'Dropdown Label',
          helperText: 'Label text for the dropdown'
        },
        {
          name: 'options',
          type: 'list',
          friendlyName: 'Dropdown Options',
          helperText: 'List of options for the dropdown',
          subFields: [
            {
              name: 'option',
              type: 'string',
              friendlyName: 'Option',
              helperText: 'Dropdown option text'
            }
          ]
        }
      ]
    }
  ]
});

Builder.registerComponent(PlanTierTable, {
  name: 'PlanTierTable',
  inputs: [
    {
      name: 'className',
      type: 'text',
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides',
      required: false
    },
    {
      name: 'topHeadline',
      type: 'text'
    },
    {
      name: 'tierItems',
      type: 'list',
      subFields: [
        {
          name: 'className',
          type: 'text',
          friendlyName: 'CSS className',
          helperText:
            'Used for Developer override styling. Keep blank if not needed',
          required: false,
          defaultValue: ''
        },
        {
          name: 'icon',
          type: 'file',
          friendlyName: 'Tier Icon',
          helperText:
            'Please use an SVG or PNG file, as this component requires transparency',
          allowedFileTypes: ['.svg', '.png'],
          required: false
        },
        {
          name: 'numberOfItems',
          type: 'text',
          friendlyName: 'Number of items',
          helperText: 'Number of items for the plan',
          required: true
        },
        {
          name: 'tierName',
          type: 'text',
          friendlyName: 'Tier name',
          helperText: 'Plan name',
          required: true
        },
        {
          name: 'tierPrice',
          type: 'text',
          friendlyName: 'Tier price',
          helperText: 'Plan price',
          required: true
        },
        {
          name: 'items',
          type: 'list',
          subFields: [
            {
              name: 'itemName',
              type: 'text',
              friendlyName: 'Item Name',
              helperText: 'Product name',
              required: true
            },
            {
              name: 'itemImage',
              type: 'object',
              required: true,
              friendlyName: 'Item image',
              helperText: 'Image fields',
              subFields: ImageInputs
            }
          ]
        }
      ]
    }
  ]
});

const siteBannerSubFields = [
  {
    name: 'bannerTextDesktop',
    type: 'string',
    required: true,
    friendlyName: 'Banner Text - Desktop Web',
    helperText: 'The main text to display in the alert bar on desktop'
  },
  {
    name: 'bannerTextMobile',
    type: 'string',
    required: true,
    friendlyName: 'Banner Text - Mobile Web',
    helperText: 'The main text to display in the alert bar on mobile'
  },
  {
    name: 'buttonTextDesktop',
    type: 'string',
    defaultValue: 'Sign up',
    friendlyName: 'Button Text - Desktop Web',
    helperText:
      'Text for the call-to-action button (leave empty to hide button)'
  },
  {
    name: 'buttonTarget',
    type: 'string',
    friendlyName: 'Button Target',
    helperText: 'Target for the button to link to'
  },
  {
    name: 'bannerTarget',
    type: 'string',
    friendlyName: 'Banner Target',
    helperText: 'Target for the banner to link to'
  },
  {
    name: 'dismissable',
    type: 'boolean',
    defaultValue: false,
    friendlyName: 'Dismissable',
    helperText: 'Whether the banner can be dismissed'
  },
  {
    name: 'openInNewTab',
    type: 'boolean',
    defaultValue: false,
    friendlyName: 'Open in New Tab',
    helperText: 'Whether to open the link in a new tab'
  },
  {
    name: 'backgroundColor',
    type: 'color',
    defaultValue: '#FFC0CB',
    friendlyName: 'Background Color',
    helperText: 'Background color for the alert bar'
  },
  {
    name: 'textColor',
    type: 'color',
    defaultValue: '#121212',
    friendlyName: 'Text Color',
    helperText: 'Text color for the alert bar'
  },
  {
    name: 'enabled',
    type: 'boolean',
    defaultValue: false,
    friendlyName: 'Enabled',
    helperText: 'Whether the banner is enabled'
  },
  {
    name: 'className',
    type: 'string',
    friendlyName: 'CSS Class Name',
    helperText: 'Optional CSS class name for custom styling'
  }
];

Builder.registerComponent(BuilderSiteBanner, {
  name: 'Site Banner',
  inputs: [
    {
      name: 'loggedOut',
      type: 'object',
      friendlyName: 'Logged Out Banner',
      helperText: 'Banner shown to users who are not logged in',
      subFields: siteBannerSubFields
    },
    {
      name: 'loggedIn',
      type: 'object',
      friendlyName: 'Logged In Banner',
      helperText: 'Banner shown to users who are logged in',
      subFields: siteBannerSubFields
    }
  ]
});

Builder.registerComponent(PromoCodeInput, {
  name: 'PromoCodeInput',
  inputs: [
    {
      name: 'tierId',
      type: 'text',
      required: true,
      friendlyName: 'Subscription Cart Product ID',
      helperText:
        'IMPORTANT: Use subscriptionProduct.productId (NOT subscriptionProduct.id) - This is the EPCC cart product ID for validation'
    },
    {
      name: 'insuranceId',
      type: 'text',
      required: true,
      friendlyName: 'Insurance Product ID',
      helperText:
        'Use insuranceProduct.id - This is the EPCC product ID for insurance validation'
    },
    {
      name: 'initialCode',
      type: 'text',
      required: false,
      friendlyName: 'Initial Promo Code',
      helperText:
        'Pre-populate with a promo code (e.g., from URL query parameter via state.initialPromoCode)'
    },
    {
      name: 'inputPlaceholder',
      type: 'text',
      defaultValue: 'Enter code',
      friendlyName: 'Input Placeholder',
      helperText: 'Placeholder text for the promo code input field'
    },
    {
      name: 'buttonText',
      type: 'text',
      defaultValue: 'Update',
      friendlyName: 'Button Text',
      helperText: 'Text for the validation button'
    },
    {
      name: 'successMessage',
      type: 'text',
      defaultValue: 'Success! {code} applied!',
      friendlyName: 'Success Message',
      helperText:
        'Message shown on successful validation. Use {code} as placeholder.'
    },
    {
      name: 'errorMessage',
      type: 'text',
      defaultValue: 'Sorry {code} is not valid!',
      friendlyName: 'Error Message',
      helperText:
        'Message shown on validation failure. Use {code} as placeholder.'
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'transparent',
      friendlyName: 'Background Color',
      helperText: 'Background color for the component'
    },
    {
      name: 'borderColor',
      type: 'color',
      defaultValue: '#D1D5DB',
      friendlyName: 'Border Color',
      helperText: 'Color for input field border'
    },
    {
      name: 'accentColor',
      type: 'color',
      defaultValue: '#FFC0CB',
      friendlyName: 'Accent Color',
      helperText: 'Color for button underline and accents'
    },
    {
      name: 'textColor',
      type: 'color',
      defaultValue: '#1F2937',
      friendlyName: 'Text Color',
      helperText: 'Primary text color'
    },
    {
      name: 'className',
      type: 'text',
      required: false,
      friendlyName: 'CSS className',
      helperText: 'CSS className used for developer overrides'
    }
  ]
});

// HYGENINJECT-REGISTER
